import { useId, useState } from "react";
import { Check, Search } from "lucide-react";
import { cx, Input } from "./ui";
import { EMOJI_CATALOG, filterEmoji, normalizeEmoji } from "@shared/emoji";

/**
 * Avatar picker: a searchable emoji grid plus a box for any emoji from the keyboard.
 * Used wherever a kid's avatar is chosen (setup, Family & kids, the kid's own profile).
 */
export function EmojiAvatarPicker({ value, onChange }: { value: string; onChange: (emoji: string) => void }) {
  const customId = useId();
  const inCatalog = EMOJI_CATALOG.some((entry) => entry.emoji === value);
  const [query, setQuery] = useState("");
  const [custom, setCustom] = useState(inCatalog ? "" : value);
  const matches = filterEmoji(query);
  // A custom pick stays visible (and selected) at the front of the grid.
  const choices = inCatalog || !value ? matches : [{ emoji: value, keywords: ["your pick"] }, ...matches];
  const customError = custom.trim() && !normalizeEmoji(custom) ? "Use just one emoji" : null;
  const customSelected = !!custom && normalizeEmoji(custom) === value;

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />
        <Input
          type="search"
          aria-label="Search emoji"
          placeholder="Search: cat, ball, star…"
          value={query}
          maxLength={40}
          onChange={(e) => setQuery(e.target.value)}
          className="py-2.5 pl-10"
        />
      </div>
      <div role="group" aria-label="Avatar choices" className="no-scrollbar grid max-h-[10.5rem] grid-cols-6 gap-1.5 overflow-y-auto p-1 sm:grid-cols-8">
        {choices.map((entry) => (
          <button
            key={entry.emoji}
            type="button"
            title={entry.keywords[0]}
            aria-label={`Avatar ${entry.emoji}`}
            aria-pressed={value === entry.emoji}
            onClick={() => {
              setCustom("");
              onChange(entry.emoji);
            }}
            className={cx(
              "flex aspect-square items-center justify-center rounded-2xl text-2xl transition",
              value === entry.emoji ? "bg-brand-soft ring-2 ring-brand" : "bg-sunken hover:bg-line/60",
            )}
          >
            {entry.emoji}
          </button>
        ))}
        {choices.length === 0 && (
          <p className="col-span-full py-3 text-center text-sm text-muted">No matches. Type any emoji below instead.</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor={customId} className="flex-1 text-sm font-bold text-ink">
          Or use any emoji from your keyboard
        </label>
        <div className="relative w-20">
          <Input
            id={customId}
            aria-label="Your own emoji"
            aria-invalid={!!customError}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder="😀"
            value={custom}
            maxLength={40}
            onChange={(e) => {
              setCustom(e.target.value);
              const emoji = normalizeEmoji(e.target.value);
              if (emoji) onChange(emoji);
            }}
            className={cx("px-2 py-2 text-center text-2xl", customSelected && "ring-2 ring-brand")}
          />
          {customSelected && (
            <Check className="absolute -right-1.5 -top-1.5 size-5 rounded-full bg-brand p-0.5 text-on-brand" aria-label="Selected" />
          )}
        </div>
      </div>
      {customError && <p className="text-sm font-semibold text-danger">{customError}</p>}
    </div>
  );
}
